import { getPeriodTitle } from 'legacy/lut/components/widgets/public/partner360/helpers/report'
import { useStaticCrud } from 'legacy/lut/hooks/useStaticCrud'
import { FC, useCallback, useMemo } from 'react'

import { Button } from 'core-components/library/button'
import { Table } from 'core-components/library/list'
import { IQuarterlyViewMarketData } from 'partner360/models/report/sobAnalyticsReports/iQuarterlyMarketTableRow'

import { QuarterlyViewMarketFooter } from './components/quarterlyViewMarketFooter/QuarterlyViewMarketFooter'
import { getQuarterlyViewMarketColumns } from './helpers/getQuarterlyViewMarketColumns'
import { IQuarterlyViewMarketTable } from './models/iQuarterlyViewMarketTable'

import styles from './QuarterlyViewMarketTable.module.scss'

export const QuarterlyViewMarketTable: FC<IQuarterlyViewMarketTable> = ({
  marketTableData,
  selectedQuarters,
  currentYear,
  selectedCategory,
  onRowToggle,
  sellType,
  showPartnersCount = true,
  marketTableTotalData,
  isExpectedExit = false,
}) => {
  const crud = useStaticCrud({
    items: marketTableData,
    hierarchyField: 'parentId',
    hierarchyRoot: null,
    keyField: 'itemId',
    sorting: {
      field: 'order',
      dir: 'asc',
    },
  })

  const isFullYear = useMemo(() => selectedQuarters.length === 4, [selectedQuarters.length])
  const previousYear = useMemo(() => currentYear - 1, [currentYear])

  const hidePlannedColumns = useMemo(() => ['TNet', 'UTNet'].includes(sellType), [sellType])

  const previousPeriodTitle = useMemo(
    () => getPeriodTitle(selectedQuarters, previousYear),
    [selectedQuarters, previousYear]
  )

  const currentPeriodTitle = useMemo(
    () => getPeriodTitle(selectedQuarters, currentYear),
    [selectedQuarters, currentYear]
  )
  const expanded = useMemo(() => {
    return marketTableData.filter((el) => el.isExpanded).map((el) => el.itemId)
  }, [marketTableData])

  const footerTpl = useCallback(() => {
    if (!marketTableTotalData || !marketTableData.length) {
      return <></>
    }
    return (
      <QuarterlyViewMarketFooter
        isFullYear={isFullYear}
        totalData={marketTableTotalData}
        sellType={sellType}
        isExpectedExit={isExpectedExit}
      />
    )
  }, [isFullYear, marketTableTotalData, marketTableData, sellType, isExpectedExit])

  const onExpandHierarchy = useCallback(
    (item: IQuarterlyViewMarketData, isExpand: boolean) => {
      if (onRowToggle) {
        onRowToggle(item.itemId, isExpand)
      }
    },
    [onRowToggle]
  )

  return (
    <Table
      header
      hasHierarchy
      source={crud}
      expanded={expanded}
      columns={getQuarterlyViewMarketColumns({
        currentPeriodTitle,
        previousPeriodTitle,
        sellType,
        selectedCategory,
        isFullYear,
        showPartnersCount,
        hidePlannedColumns,
        isExpectedExit,
      })}
      classes={{
        root: styles.table,
        body: styles.tBody,
      }}
      footerTpl={showPartnersCount ? footerTpl : undefined}
      hierarchyTpl={(item, rowState, hierarchyClick) => {
        const hasChildren = item.hasChildren
        const isExpanded = rowState.expanded

        return hasChildren ? (
          <td>
            <Button
              onClick={() => {
                hierarchyClick(item)
                onExpandHierarchy(item, !isExpanded)
              }}
              iconButton={`kbase ${rowState.expanded ? 'kbase-caret-down' : 'kbase-caret-right'}`}
              size={'sm'}
              className={styles.hierarchyButton}
              variant={'text'}
            />
          </td>
        ) : (
          <td></td>
        )
      }}
    />
  )
}
